import { ComponentPropsWithoutRef, useCallback, useRef } from "react";
import Icon from "~/components/shared/Icon";
import { useTrack } from "~/components/build/Show/lib/useTrack";
import { twMerge } from "tailwind-merge";
import { useFocusOnFailure } from "~/components/build/Show/components/StepDrawer/GoToFailure/useFocusOnFailure";

interface GoToFailureProps extends Omit<ComponentPropsWithoutRef<"button">, "onClick"> {
  className?: string;
}

export const GoToFailure = ({ className = "", ...props }: GoToFailureProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const track = useTrack();
  const { focusOnFailure, hasNextFailure } = useFocusOnFailure();

  const handleClick = useCallback(() => {
    focusOnFailure();
    track("Click Go To Failure");

    // Blur the button to prevent focus issues when navigating.
    if (ref.current) {
      ref.current.blur();
    }
  }, [focusOnFailure, track]);

  return (
    <button
      {...props}
      ref={ref}
      onClick={handleClick}
      disabled={!hasNextFailure}
      title="Go to next failure (F)"
      className={twMerge(
        "hover:bg-text-600 group/button text-charcoal-900 inline-flex h-7 w-7 items-center justify-center rounded-md bg-gray-100 font-medium transition-all hover:bg-purple-100 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
    >
      <Icon
        icon="custom/outline/redo-dot"
        className="h-4 w-4 group-hover/button:stroke-purple-600"
      />
    </button>
  );
};
